/**
 * Surveys — admin side of the questionnaire system.
 *
 * The frozen contract lives in the BFF repo, at `docs/features/surveys-optin/03-contratos.md`
 * (section `ADMIN · Surveys`) and `03-tipos.ts`. What a dashboard user receives (`SurveyPublic`)
 * is a translated, single-language projection of {@link Survey}; this file is the admin view,
 * which carries every language, every version marker and the moderation fields.
 *
 * Where this file goes **beyond** `03-tipos.ts`, it is because the admin `GET /v1/surveys/:id`
 * answers more than the frozen document describes — `reviewStatus`, `workingVersion`,
 * `draftVersion`, `supportedLanguages`, `responseCount` and the per-row `translations` arrays.
 * Those are read from the BFF's own `mapSurvey`, not invented here.
 *
 * @module Pages/BUs/Surveys
 */

/** The three languages every tenant supports. Base columns are English; the rest are translations. */
export const SURVEY_LANGUAGES = ['en', 'pt', 'es'] as const;

export type SurveyLanguage = (typeof SURVEY_LANGUAGES)[number];

/**
 * The language the **admin screens** ask the BFF to resolve labels in.
 *
 * The panel is written in pt-BR, and without this the aggregates come back with the base column —
 * question labels, option labels and opt-out reasons in English next to Portuguese chrome. The
 * identifiers (`key`, option `value`) stay raw either way: they are stable codes, and they are how
 * the CSV and the comparison between versions work.
 *
 * **This depends on the BFF accepting the parameter.** The global `ValidationPipe` runs with
 * `forbidNonWhitelisted: true`, so an endpoint whose query DTO does not declare `language` answers
 * `400 property language should not exist` and the results screen does not load. Set this to
 * `null` if that ever happens; nothing else has to change.
 */
export const ADMIN_LABEL_LANGUAGE: string | null = 'pt';

export const SURVEY_LANGUAGE_LABELS: Record<SurveyLanguage, string> = {
  en: 'Inglês',
  pt: 'Português',
  es: 'Espanhol',
};

export const SURVEY_QUESTION_TYPES = [
  'SHORT_TEXT',
  'LONG_TEXT',
  'SINGLE_CHOICE',
  'MULTI_CHOICE',
  'RATING',
  'NPS',
  'BOOLEAN',
  'MATRIX',
] as const;

export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPES)[number];

/** R25 — what a grid row is allowed to be. A row never holds a scale of its own. */
export const MATRIX_ROW_TYPES = ['SINGLE_CHOICE', 'RATING', 'NPS', 'BOOLEAN'] as const;

export type MatrixRowType = (typeof MATRIX_ROW_TYPES)[number];

/** Types that carry their own option list. */
export const OPTION_BEARING_TYPES: SurveyQuestionType[] = [
  'SINGLE_CHOICE',
  'MULTI_CHOICE',
  'MATRIX',
];

/**
 * How the survey reaches a person.
 *
 * `ON_OPT_OUT` is the exit questionnaire of an opt-in feature, and it is the one trigger that is
 * **never delivered on its own** (R34): it is out of the automatic queue and out of the on-demand
 * list, and the single way to it is the `exitSurveyId` the bootstrap hangs off the feature — which
 * comes from `requiresOptInFeatureId`. A survey with this trigger and no feature would be
 * approved, active and asked to nobody, so the BFF answers `400` (and this screen refuses first).
 */
export type SurveyTrigger = 'AUTO' | 'ON_DEMAND' | 'ON_OPT_OUT';
export type SurveyLayout = 'SINGLE_PAGE' | 'ONE_PER_STEP';
export type SurveyApprovalStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
export type SurveyResponseStatus = 'IN_PROGRESS' | 'SUBMITTED' | 'DISMISSED';
export type OptInState = 'ENABLED' | 'DISABLED' | 'ANY';
export type DelayAnchor = 'FIRST_ENABLED_AT' | 'LAST_ENABLED_AT' | 'DISABLED_AT';
/**
 * Unit of `delayAmount`. `MINUTES` and `HOURS` exist so the rule can be watched working on the day
 * of the deploy instead of being confirmed a week later.
 *
 * The BFF reads an absent unit as `DAYS` and does **not** refuse an amount without one, so the
 * builder always sends the pair: "5" meant as minutes and saved as days is five days nobody
 * notices, and no error anywhere says so.
 */
export type DelayUnit = 'MINUTES' | 'HOURS' | 'DAYS';

/** R24 — the ceilings the BFF refuses to cross, repeated here so the editor can say so first. */
export const SURVEY_LIMITS = {
  /** Parent questions and grid rows together. */
  questions: 50,
  optionsPerQuestion: 30,
  steps: 10,
  rowsPerMatrix: 20,
} as const;

/** Per-type settings. Every field optional — the renderer applies the default. */
export interface SurveyQuestionConfig {
  /** RATING */
  min?: number;
  max?: number;
  labels?: Record<string, string>;
  /** NPS */
  leftLabel?: string;
  rightLabel?: string;
  /** BOOLEAN */
  trueLabel?: string;
  falseLabel?: string;
  /** SHORT_TEXT / LONG_TEXT */
  maxLength?: number;
  placeholder?: string;
  /** MULTI_CHOICE */
  minSelections?: number;
  maxSelections?: number;
}

export interface SurveyLabelTranslation {
  language: string;
  label: string;
  helpText?: string | null;
  config?: SurveyQuestionConfig;
}

export interface SurveyQuestionOption {
  id: string;
  /** Stable code, written into every answer row. Never translated (R5's sibling). */
  value: string;
  label: string;
  order: number;
  isExclusive: boolean;
  allowsFreeText: boolean;
  translations?: SurveyLabelTranslation[];
}

/** One row of a grid. Inherits the scale, the config and the step of its parent (R25). */
export interface SurveyMatrixRow {
  id: string;
  key: string;
  type: MatrixRowType;
  label: string;
  helpText: string | null;
  isRequired: boolean;
  order: number;
  translations?: SurveyLabelTranslation[];
}

export interface SurveyQuestion {
  id: string;
  /** CSV column name and the lineage between versions. Frozen once answered (R5). */
  key: string;
  type: SurveyQuestionType;
  label: string;
  helpText: string | null;
  isRequired: boolean;
  step: number;
  order: number;
  config: SurveyQuestionConfig;
  /** Always an array — empty when the type carries no options. */
  options: SurveyQuestionOption[];
  /** Always an array — filled only on MATRIX. */
  rows: SurveyMatrixRow[];
  translations?: SurveyLabelTranslation[];
}

export interface SurveyTranslation {
  language: string;
  title: string;
  description?: string | null;
}

export interface SurveyNamedRef {
  id: string;
  name: string;
  slug?: string;
}

export interface SurveyOptInRef {
  id: string;
  key: string;
  name: string;
}

export interface SurveyUserRef {
  id: string;
  name?: string | null;
  email?: string | null;
}

/**
 * A survey as the admin endpoints answer it.
 *
 * Three version fields, and telling them apart matters:
 * - `version` is the one **being delivered** right now;
 * - `draftVersion` is the one **being written**, `null` when there is none;
 * - `workingVersion` is `draftVersion ?? version` — the one the questionnaire below belongs to.
 *
 * `approvalStatus` describes the **delivered** version, so an approved survey with an open draft
 * stays `APPROVED` and keeps being delivered while the draft is reviewed (R4). The state of the
 * draft is `reviewStatus`.
 */
export interface Survey {
  id: string;
  tenantSlug: string;
  slug: string;
  version: number;
  draftVersion: number | null;
  workingVersion: number;
  /** Smallest version that counts as answered. Raised by `bump-version` with `reask: true` (R3). */
  reaskFromVersion: number;
  title: string;
  description: string | null;
  layout: SurveyLayout;
  trigger: SurveyTrigger;
  priority: number;
  isActive: boolean;
  opensAt: string | null;
  closesAt: string | null;
  snoozeDays: number | null;
  page: SurveyNamedRef | null;
  useCase: SurveyNamedRef | null;
  requiresOptInFeature: SurveyOptInRef | null;
  requiredOptInState: OptInState | null;
  delayAmount: number | null;
  delayUnit: DelayUnit | null;
  delayAnchor: DelayAnchor | null;
  eligibleOptInFrom: string | null;
  maxDaysSinceAnchor: number | null;
  approvalStatus: SurveyApprovalStatus;
  /** State of the version under review — the draft when one is open. */
  reviewStatus: SurveyApprovalStatus;
  createdBy?: SurveyUserRef | null;
  approvedBy?: SurveyUserRef | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * When the survey was soft-deleted, or `null`/absent while it is live.
   *
   * Only arrives when the listing was asked for `includeDeleted=true`. It is what separates a
   * deleted row from a live one **inside the same list** — there is no separate listing, on
   * purpose: the slug a deleted survey is holding only means something next to the surveys still
   * using theirs.
   */
  deletedAt?: string | null;
  /** Languages the tenant declares. `approve` refuses while one of them is missing (R6). */
  supportedLanguages?: string[];
  translations: SurveyTranslation[];
  /** Responses across every version and every status. Zero is what unlocks structural edits. */
  responseCount: number;
  /** Only on `GET /:id` — the questionnaire of `workingVersion`. */
  questions?: SurveyQuestion[];
}

export type SurveyDetail = Survey & { questions: SurveyQuestion[] };

export interface SurveyListRequest {
  /**
   * Brings the deleted surveys **into** the listing, next to the live ones. Default `false`.
   *
   * Not "only the deleted": the `includeDeleted` of the e-mail signatures module means the
   * opposite, and reading this one that way would quietly empty the screen.
   */
  includeDeleted?: boolean;
  tenantSlug?: string;
  approvalStatus?: SurveyApprovalStatus;
  trigger?: SurveyTrigger;
  isActive?: boolean;
  requiresOptInFeatureId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/** `GET /v1/surveys` answers a flat envelope, not `{ data, pagination }`. */
export interface SurveyListResponse {
  data: Survey[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SurveyCreatePayload {
  tenantSlug: string;
  slug: string;
  title: string;
  description?: string;
  layout?: SurveyLayout;
  trigger?: SurveyTrigger;
  priority?: number;
  isActive?: boolean;
  opensAt?: string;
  closesAt?: string;
  snoozeDays?: number;
  pageId?: string;
  useCaseId?: string;
  requiresOptInFeatureId?: string;
  requiredOptInState?: OptInState;
  delayAmount?: number;
  delayUnit?: DelayUnit;
  delayAnchor?: DelayAnchor;
  eligibleOptInFrom?: string;
  maxDaysSinceAnchor?: number;
  translations?: SurveyTranslation[];
}

/** `tenantSlug` is absent by design: moving a survey between tenants orphans its responses. */
export type SurveyUpdatePayload = Partial<Omit<SurveyCreatePayload, 'tenantSlug'>>;

export interface SurveyOptionInput {
  value: string;
  label: string;
  order: number;
  isExclusive?: boolean;
  allowsFreeText?: boolean;
  translations?: { language: string; label: string }[];
}

export interface SurveyMatrixRowInput {
  /** The row's current id, when it already exists. Proves to the BFF the `key` did not move. */
  id?: string;
  key?: string;
  type: MatrixRowType;
  label: string;
  helpText?: string;
  isRequired?: boolean;
  order: number;
  translations?: SurveyLabelTranslation[];
}

export interface SurveyQuestionInput {
  /** The question's current id, when it already exists. Same purpose as the row's. */
  id?: string;
  key?: string;
  type: SurveyQuestionType;
  label: string;
  helpText?: string;
  isRequired?: boolean;
  step?: number;
  order: number;
  config?: SurveyQuestionConfig;
  options?: SurveyOptionInput[];
  rows?: SurveyMatrixRowInput[];
  translations?: SurveyLabelTranslation[];
}

export interface SaveQuestionsPayload {
  questions: SurveyQuestionInput[];
}

/* ─────────────────────────────── Results ─────────────────────────────── */

/**
 * Which population the response rate was measured against.
 *
 * `EVERYONE` means the survey has no opt-in condition, so it reaches every platform user of the
 * tenant — a roster that lives in the org backend, not in the BFF. `totalEligible` and
 * `responseRate` then come back `null`, and the screen writes the audience out in words instead
 * of printing a fraction it cannot compute.
 */
export type SurveyEligibilityScope = 'OPT_IN' | 'EVERYONE';

export interface SurveyFunnelStep {
  step: number;
  reached: number;
  /** Three cuts, because they measure different things and must not collapse into one. */
  avgMsAll: number | null;
  avgMsCompleted: number | null;
  /** IN_PROGRESS + DISMISSED: everything that was not sent. */
  avgMsAbandoned: number | null;
  /** Reached this step and not the next. On the last: and never sent. */
  dropped: number;
}

export interface SurveyDistributionEntry {
  /** A number in RATING/NPS; an option `value` in a grid row. */
  value: number | string;
  count: number;
}

export interface SurveyOptionResult {
  value: string;
  label: string;
  count: number;
  /** Of whoever ANSWERED the question — in multiple choice the sum goes past 1. */
  percent: number;
}

export interface SurveyMatrixRowResult {
  key: string;
  label: string;
  type: SurveyQuestionType;
  answered: number;
  /** Position on the scale (1 = first option). `null` on a BOOLEAN row. */
  average: number | null;
  distribution: SurveyDistributionEntry[];
}

/** Which fields are present depends on `type` — the same switch the renderer uses. */
export interface SurveyQuestionResult {
  key: string;
  type: SurveyQuestionType;
  label: string;
  answered: number;

  // RATING / NPS
  average?: number | null;
  distribution?: SurveyDistributionEntry[];

  // NPS
  score?: number | null;
  promoters?: number;
  passives?: number;
  detractors?: number;

  // SINGLE_CHOICE / MULTI_CHOICE
  options?: SurveyOptionResult[];

  // BOOLEAN
  trueCount?: number;
  falseCount?: number;
  truePercent?: number;

  // MATRIX
  scale?: { value: string; label: string }[];
  rows?: SurveyMatrixRowResult[];

  // SHORT_TEXT / LONG_TEXT: only `answered`. The content comes from `GET /:id/comments`.
}

/**
 * The adoption of the feature this survey hangs off — **adoption only**.
 *
 * Why somebody left is no longer here: `topReasons` went with the `reason` column (BFF 5.0.0). In
 * an exit questionnaire that question is answered by `questions` above, aggregated per question, in
 * the words the people actually read.
 */
export interface SurveyResultsOptIn {
  featureKey: string;
  featureName: string;
  everEnabled: number;
  enabledNow: number;
  reverted: number;
  revertRate: number;
  /** Answered the survey AND has the feature off right now. */
  respondentsWhoReverted: number;
}

export interface SurveyResults {
  surveyId: string;
  slug: string;
  /** Always one version — never pooled across versions, never across surveys (R21). */
  version: number;
  eligibilityScope: SurveyEligibilityScope;
  /** `null` when `eligibilityScope === 'EVERYONE'`. */
  totalEligible: number | null;
  /** Responses in any status — everyone who opened it. */
  totalStarted: number;
  totalSubmitted: number;
  totalDismissed: number;
  totalInProgress: number;
  /** `null` when `eligibilityScope === 'EVERYONE'`. */
  responseRate: number | null;
  /** Average over SUBMITTED only (R14). */
  avgDurationMs: number | null;
  /**
   * NEVER narrowed by a cut (R49) — whoever abandoned at step 1 left no answer at all, so no cut
   * by an answer can reach them, and a "cut" funnel would describe a path nobody walked.
   */
  funnel: SurveyFunnelStep[];
  /** The ONLY block a cut narrows (R49). With a cut on, its base is in `filter`. */
  questions: SurveyQuestionResult[];
  /** ABSENT — not `null` — when no cut was asked for (R49). */
  filter?: SurveyResultsFilter;
  /** ABSENT when the survey hangs off no opt-in feature. */
  optIn?: SurveyResultsOptIn;
}

/**
 * The cut: one question, one of its options, and both always together (R49).
 *
 * Sending one without the other is a `400` — half a cut names no group of people — which is why
 * nothing on the screen can produce one half: a single control emits the pair or nothing.
 */
export interface SurveyResultsCut {
  /** `key` of a CHOICE question of the SAME version. */
  filterQuestionKey: string;
  /** The option's `value` — the code, never the translated label. */
  filterOptionValue: string;
}

/**
 * Which base each number used, present only when a cut was applied (R49).
 *
 * `people` is the base of `questions[]`. `totalSubmitted` is the base of everything else — the
 * same number as the field at the top, repeated here so the screen can write "de 40 das 214
 * pessoas" without reading from two places. A percentage with no base stated is the very defect
 * this screen exists to fight.
 *
 * Both labels arrive already resolved in `?language`: the screen never rebuilds them.
 */
export interface SurveyResultsFilter {
  questionKey: string;
  questionLabel: string;
  optionValue: string;
  optionLabel: string;
  /** PEOPLE in the cut. Zero is a legitimate result, never an error. */
  people: number;
  /** PEOPLE who submitted, with no cut. */
  totalSubmitted: number;
}

export interface SurveyAnswerOptionView {
  value: string;
  label: string;
}

export interface SurveyAnswerView {
  key: string;
  type: SurveyQuestionType;
  valueText: string | null;
  valueNumber: number | null;
  valueBool: boolean | null;
  otherText: string | null;
  options: SurveyAnswerOptionView[];
}

export interface SurveyResponseView {
  id: string;
  platformUserId: number;
  status: SurveyResponseStatus;
  surveyVersion: number;
  language: string | null;
  /** Days of feature use at the moment they sent it (R17). `null` on an unconditional survey. */
  daysSinceAnchor: number | null;
  stepsSeen: number | null;
  durationMs: number | null;
  dismissCount: number;
  startedAt: string;
  submittedAt: string | null;
  answers: SurveyAnswerView[];
}

export interface SurveyResponsesPage {
  responses: SurveyResponseView[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SurveyResponsesRequest {
  version?: number;
  status?: SurveyResponseStatus;
  page?: number;
  limit?: number;
}

/**
 * What `POST /:id/purge` answers with (R26) — how many rows went.
 *
 * Every version and every status, and only the responses: the survey, its questionnaire and its
 * translations survive. Answers, selected options and step timings follow by cascade.
 */
export interface SurveyPurgeResult {
  deletedResponses: number;
}

/**
 * What `POST /:id/reset` answers with (R42) — the damage in numbers, plus where the survey landed.
 *
 * The three counters are not decoration. A reset erases answers **and** versions, and a screen that
 * says "pronto" after an irreversible operation leaves nobody able to check that what happened was
 * what they asked for. The four state fields are what lets the builder redraw itself without
 * guessing: `approvalStatus` in particular comes back **unchanged**, because resetting neither
 * demotes nor approves.
 */
export interface SurveyResetResult {
  /** Across every version. */
  deletedResponses: number;
  /** Versions above the first that had a questionnaire. The v1 questionnaire is kept. */
  deletedVersions: number;
  /** Rewording records of R39. */
  deletedQuestionEdits: number;
  version: number;
  draftVersion: number | null;
  reaskFromVersion: number;
  approvalStatus: SurveyApprovalStatus;
}

/**
 * What `DELETE /:id/responses/:platformUserId` answers with (R27).
 *
 * `deletedResponses` can be more than one: the erasure crosses **every version** of the survey, so
 * somebody who answered version 1 and again version 2 loses both rows in the same call.
 */
export interface SurveyRespondentErasure {
  platformUserId: number;
  deletedResponses: number;
  message: string;
}

export type SurveyCommentSource = 'ANSWER' | 'OTHER_TEXT';

export interface SurveyComment {
  responseId: string;
  platformUserId: number;
  questionKey: string;
  questionLabel: string;
  questionType: SurveyQuestionType;
  /** `OTHER_TEXT` = the box behind an "Other: ___" option. */
  source: SurveyCommentSource;
  text: string;
  language: string | null;
  submittedAt: string | null;
}

export interface SurveyCommentsPage {
  comments: SurveyComment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  /** ABSENT when no cut was asked for (R49). The same block `/results` returns. */
  filter?: SurveyResultsFilter;
}

/**
 * `questionKey` and `filterQuestionKey` are different things and combine.
 *
 * The first narrows **which question** shows up in the feed; the second narrows **whose** text is
 * in it: "the comments on question X written by whoever marked Y on question Z".
 */
export interface SurveyCommentsRequest extends Partial<SurveyResultsCut> {
  version?: number;
  questionKey?: string;
  page?: number;
  limit?: number;
}

export interface SurveyStatsSummary {
  total: number;
  draft: number;
  pendingApproval: number;
  approved: number;
  rejected: number;
  live: number;
  withDraftVersion: number;
  totalSubmittedResponses: number;
}

export interface SurveyErrorResponse {
  message: string;
  statusCode?: number;
}

/** A CSV the browser is about to save. The filename comes from the BFF's own header. */
export interface SurveyCsvFile {
  filename: string;
  blob: Blob;
}
